import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

import StatusBadge from '../../shared/StatusBadge';
import { getOrganizationId } from '../../../store';
import { TOKENS, Plate, CLOCK_IN_TARGET_STATES } from './worktimeLib';

/**
 * Picker of work orders a mechanic can clock into. Uses the verified `state` filter
 * (BaseInFilter, comma-separated → current_state__in) including waiting/vehicle_in_workshop —
 * resuming a parts-wait / in-workshop car is a normal target. For employees the list is already
 * server-scoped to assigned orders.
 */
export default function WorkOrderPicker({ open, onClose, onSelect, excludeMaintenanceId }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const orgId = getOrganizationId();
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`/api/maintenances/`, {
        params: { organization: orgId, state: CLOCK_IN_TARGET_STATES.join(',') },
      })
      .then((res) => {
        if (cancelled) return;
        const results = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setItems(results.filter((m) => m.id !== excludeMaintenanceId));
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, excludeMaintenanceId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{t('worktime.picker.title')}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography sx={{ color: TOKENS.muted, py: 3, textAlign: 'center' }}>
            {t('worktime.picker.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((m) => (
              <ListItemButton
                key={m.id}
                onClick={() => onSelect(m)}
                sx={{ borderRadius: 2, mb: 1, border: `1px solid ${TOKENS.border}`, gap: 1.5, py: 1.5 }}
              >
                <PlayCircleIcon sx={{ color: TOKENS.green }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Plate size={16}>{m.vehicle?.registration_number || '—'}</Plate>
                  <Typography sx={{ fontSize: 13, color: TOKENS.muted, overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
                    {[m.vehicle?.manufacturer, m.vehicle?.model].filter(Boolean).join(' ') || m.description}
                  </Typography>
                </Box>
                <StatusBadge status={m.state} size="small" />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
